import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Ellipsis, Pencil, Pin, PinOff, Plus, Trash2, User, CalendarDays, TrendingUp, House } from 'lucide-react';
import './LeftSidebar.css';

function LeftSidebar({
  isOpen,
  dashboards,
  activeDashboardId,
  onSelectDashboard,
  onAddDashboard,
  onRenameDashboard,
  onTogglePinDashboard,
  onDeleteDashboard,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const handleDashboardClick = (dashboard) => {
    onSelectDashboard(dashboard.id);
    navigate(dashboard.path);
  };

  const handleDoubleClick = (dashboard) => {
    setOpenMenuId(null);
    setEditingId(dashboard.id);
    setEditValue(dashboard.name);
  };

  const startRename = (dashboard) => {
    setOpenMenuId(null);
    setEditingId(dashboard.id);
    setEditValue(dashboard.name);
  };

  const commitRename = () => {
    if (editingId && editValue.trim()) {
      onRenameDashboard(editingId, editValue.trim());
    }
    setEditingId(null);
    setEditValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      commitRename();
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditValue('');
    }
  };

  // Focus the input when editing starts
  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`left-sidebar ${isOpen ? 'left-sidebar--open' : ''}`}>
      {/* ── Top tabs ── */}
      {/* <div className="left-sidebar__tabs">
        <span className="left-sidebar__tab left-sidebar__tab--active">Favorites</span>
        <span className="left-sidebar__tab">Recently</span>
      </div> */}

      {/* ── Nav items (non-clickable) ── */}
      <div className="left-sidebar__nav-section">
        <button
          className={`left-sidebar__nav-item left-sidebar__nav-item--clickable ${location.pathname === '/home' || location.pathname === '/' ? 'left-sidebar__nav-item--active' : ''}`}
          onClick={() => navigate('/home')}
        >
          <House size={16} strokeWidth={1.5} className="left-sidebar__nav-icon" />
          Home
        </button>
        <button
          className={`left-sidebar__nav-item left-sidebar__nav-item--clickable ${location.pathname === '/my-info' ? 'left-sidebar__nav-item--active' : ''}`}
          onClick={() => navigate('/my-info')}
        >
          <User size={16} strokeWidth={1.5} className="left-sidebar__nav-icon" />
          My Info
        </button>
        <button
          className={`left-sidebar__nav-item left-sidebar__nav-item--clickable ${location.pathname === '/calendar' ? 'left-sidebar__nav-item--active' : ''}`}
          onClick={() => navigate('/calendar')}
        >
          <CalendarDays size={16} strokeWidth={1.5} className="left-sidebar__nav-icon" />
          Calendar
        </button>
        <button
          className={`left-sidebar__nav-item left-sidebar__nav-item--clickable ${location.pathname === '/progress' ? 'left-sidebar__nav-item--active' : ''}`}
          data-tour="progress-nav"
          onClick={() => navigate('/progress')}
        >
          <TrendingUp size={16} strokeWidth={1.5} className="left-sidebar__nav-icon" />
          Progress
        </button>
      </div>

      {/* ── Dashboards section ── */}
      <div className="left-sidebar__dashboards-section">
        <span className="left-sidebar__section-label">Dashboards</span>

        <div className="left-sidebar__dashboard-list">
          {dashboards.map((dashboard) => (
            <React.Fragment key={dashboard.id}>
              {editingId === dashboard.id ? (
                <input
                  ref={inputRef}
                  className="left-sidebar__dashboard-rename-input"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={commitRename}
                />
              ) : (
                <div
                  className={`left-sidebar__dashboard-row ${activeDashboardId === dashboard.id ? 'left-sidebar__dashboard-row--active' : ''}`}
                  ref={openMenuId === dashboard.id ? menuRef : null}
                >
                  <button
                    className="left-sidebar__dashboard-item"
                    onClick={() => handleDashboardClick(dashboard)}
                    onDoubleClick={() => handleDoubleClick(dashboard)}
                  >
                    <span className="left-sidebar__dashboard-name">{dashboard.name}</span>
                    {dashboard.pinned && (
                      <Pin size={12} strokeWidth={1.8} className="left-sidebar__pin-icon" />
                    )}
                  </button>
                  <button
                    className={`left-sidebar__dashboard-menu-btn ${openMenuId === dashboard.id ? 'left-sidebar__dashboard-menu-btn--open' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId((id) => (id === dashboard.id ? null : dashboard.id));
                    }}
                    aria-label={`Open actions for ${dashboard.name}`}
                  >
                    <Ellipsis size={15} strokeWidth={2} />
                  </button>

                  {openMenuId === dashboard.id && (
                    <div className="left-sidebar__dashboard-menu">
                      <button
                        className="left-sidebar__dashboard-menu-item"
                        onClick={() => startRename(dashboard)}
                      >
                        <Pencil size={13} strokeWidth={1.8} />
                        Rename
                      </button>
                      <button
                        className="left-sidebar__dashboard-menu-item"
                        onClick={() => {
                          setOpenMenuId(null);
                          onTogglePinDashboard(dashboard.id);
                        }}
                      >
                        {dashboard.pinned ? (
                          <PinOff size={13} strokeWidth={1.8} />
                        ) : (
                          <Pin size={13} strokeWidth={1.8} />
                        )}
                        {dashboard.pinned ? 'Unpin' : 'Pin'}
                      </button>
                      <button
                        className="left-sidebar__dashboard-menu-item left-sidebar__dashboard-menu-item--danger"
                        disabled={dashboards.length <= 1}
                        onClick={() => {
                          setOpenMenuId(null);
                          onDeleteDashboard(dashboard.id);
                        }}
                      >
                        <Trash2 size={13} strokeWidth={1.8} />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <button className="left-sidebar__add-btn" data-tour="add-dashboard" onClick={onAddDashboard}>
          <Plus size={14} strokeWidth={2} />
          Add Dashboard
        </button>
      </div>
    </div>
  );
}

export default LeftSidebar;
